import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import React, { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import {
	ChangesSource,
	getRolePriority,
	NotificationType,
	NurseFullRoleShort,
	NurseRole,
	type OpenShiftRequest,
	type ReplaceShiftRequest,
	RequestResponsesStatusOptions,
	ShiftType,
} from "../../utils/models";
import { hasNurseResponse } from "../../utils/utilFunctions";
import { Recommendation } from "../Recommendation/Recommendation";
import { type SystemChipItem, SystemChips } from "../UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { useUpdateRequestTime } from "./hooks/useUpdateRequestTime";
import { useRequestUtils } from "./Provider/useRequestUtils";

export interface RequestsProps {
	isRequestedTab?: boolean;
	isTitleOnly?: boolean;
	isFromHeader?: boolean;
	isAutoScheduler?: boolean;
	date?: DateTime;
	assignments?: string[];
}

enum RequestsChipType {
	withResponse = 0,
	withoutResponse = 1,
	replaceRequest = 2,
}

const RequestsTabItems: SystemChipItem<RequestsChipType[]>[] = [
	{
		value: [RequestsChipType.withResponse, RequestsChipType.withoutResponse, RequestsChipType.replaceRequest],
		label: "All",
	},
	{
		value: [RequestsChipType.withResponse],
		label: "Answered",
	},
	{
		value: [RequestsChipType.withoutResponse],
		label: "Unanswered",
	},
	{
		value: [RequestsChipType.replaceRequest],
		label: "Replace",
	},
];

const Requests: FC<RequestsProps> = ({ isRequestedTab = false, isTitleOnly = false, isFromHeader = false, isAutoScheduler, date, assignments }) => {
	const { t } = useTranslation();
	const { hospitalData, chosenDepartment, setImplementedChanges, implementedChanges } = useHospitalData();
	const { recomendationFromRequests } = useRequestUtils();
	const [selectedChip, setSelectedChip] = useState<SystemChipItem<RequestsChipType[]>>(RequestsTabItems[0]);
	const { weekIndex } = useWeekIndex();
	const { setStaffRequests } = useRequestsContext();
	const navigate = useNavigate();
	const { staffRequests } = useRequestsContext();
	const { handleRequestTimeChange } = useUpdateRequestTime();
	const handleChipChange = useCallback((item: SystemChipItem<RequestsChipType[]>, selected: boolean) => {
		setSelectedChip((prevState) => {
			if (selected) {
				return item;
			}
			return prevState;
		});
	}, []);

	const isRequestValidByFilters = useCallback(
		(request: OpenShiftRequest | ReplaceShiftRequest): boolean => {
			return (
				(selectedChip.value.includes(RequestsChipType.replaceRequest) && request.request_type === NotificationType.ReplaceShift) ||
				(selectedChip.value.includes(RequestsChipType.withoutResponse) && !hasNurseResponse(request)) ||
				(selectedChip.value.includes(RequestsChipType.withResponse) && hasNurseResponse(request))
			);
		},
		[selectedChip],
	);

	const filteredRequests = React.useMemo(() => {
		if (!date || !staffRequests) return staffRequests.filter((request) => isRequestValidByFilters(request));

		return staffRequests.filter((request) => {
			const requestDate = request.message?.date;
			return requestDate.hasSame(date, "day") && isRequestValidByFilters(request);
		});
	}, [staffRequests, date, isRequestValidByFilters]);

	useEffect(() => {
		if (!chosenDepartment?.department_id || !staffRequests) return;
	}, [chosenDepartment?.department_id, staffRequests]);

	const handleNotificationClick = useCallback(
		(notification: any) => {
			if (isFromHeader) {
				navigate("/staffing?openNotifications=true");
			}
		},
		[navigate, isFromHeader],
	);

	const handleRequestChange = useCallback(
		(requestId: string | number, suggestionIdx: number, isCancel: boolean, nurse_id?: number) => {
			const requestIndex = staffRequests.findIndex((request) => request.id === requestId);

			if (requestIndex < 0) return;

			const request = staffRequests[requestIndex];
			// It means cancel all the notification
			if (suggestionIdx === -1 && isCancel) {
				return staffRequests.filter((n) => n.id !== requestId);
			}

			const nurseToUpdate: number =
				nurse_id ?? request.message.request_data.filter((r) => r.status === RequestResponsesStatusOptions.PENDING)[suggestionIdx].nurse_id;
			const newRequests = [];
			const updateDuplicateDeclinedRequests: string[] = [];

			for (let i = 0; i < staffRequests.length; i++) {
				const currRequest = staffRequests[i];
				if (i === requestIndex) {
					const shouldRemove =
						!isCancel &&
						(currRequest.request_type === NotificationType.ReplaceShift ||
							(currRequest.request_type === NotificationType.OpenShift && (currRequest as OpenShiftRequest).needed_nurses === 1));
					if (shouldRemove) {
						continue;
					}

					const request_copy = {
						...currRequest,
						message: {
							...currRequest.message,
							request_data: currRequest.message.request_data.filter(
								(r) => !(r.status === RequestResponsesStatusOptions.PENDING && r.nurse_id === nurseToUpdate),
							),
						},
					};
					if (!isCancel) {
						// It means it's an openShift with more then 1 needed nurses
						(request_copy as OpenShiftRequest).needed_nurses--;
					}
					newRequests.push(request_copy);
				} else if (
					!isCancel &&
					currRequest.message.shift_start === request.message.shift_start &&
					currRequest.message.shift_end === request.message.shift_end
				) {
					const request_copy = {
						...currRequest,
						message: {
							...currRequest.message,
							request_data: currRequest.message.request_data.filter(
								(r) => !(r.status === RequestResponsesStatusOptions.PENDING && r.nurse_id === nurseToUpdate),
							),
						},
					};
					if (request_copy.message.request_data.length !== currRequest.message.request_data.length) {
						updateDuplicateDeclinedRequests.push(currRequest.id);
					}
					newRequests.push(request_copy);
				} else {
					newRequests.push(currRequest);
				}
			}

			setStaffRequests(newRequests);
		},
		[staffRequests, setStaffRequests],
	);

	const sortRequests = useCallback((a: OpenShiftRequest | ReplaceShiftRequest, b: OpenShiftRequest | ReplaceShiftRequest) => {
		// This function sorts notifications based on multiple criteria in the following order:
		// 1. Date (earlier dates first)
		// 2. Shift type (day shifts before night shifts)
		// 3. Notification type (open shifts before replace shifts)
		// 4. Number of needed nurses (higher number first)
		// 5. Number of accepted nurses (higher number first)
		// 6. Number of roles needed (higher number first)
		// 7. Role priority (RN > LPN, LVN > CNA > CMA > RNA > MT > NAS > RA > RD > DA > CK)
		// 8. ID (lower ID first)

		const dateA = a.message?.date;
		const dateB = b.message?.date;
		// First sort by date
		if (dateA !== dateB) {
			return dateA < dateB ? -1 : 1;
		}
		// If same date, sort by shift type (day before night)
		const shiftTypeA = a.message?.shift_type;
		const shiftTypeB = b.message?.shift_type;
		if (shiftTypeA === ShiftType.Day && shiftTypeB === ShiftType.Night) {
			return -1; // Day comes before Night
		}
		if (shiftTypeA === ShiftType.Night && shiftTypeB === ShiftType.Day) {
			return 1; // Night comes after Day
		}
		// Sort by notification type (open_shift before replace_shift)
		if (a.request_type === NotificationType.OpenShift && b.request_type === NotificationType.ReplaceShift) {
			return -1; // OpenShift comes before ReplaceShift
		}
		if (a.request_type === NotificationType.ReplaceShift && b.request_type === NotificationType.OpenShift) {
			return 1; // ReplaceShift comes after OpenShift
		}
		// Sort by number of needed nurses (higher number first)
		const neededNursesA = (a as OpenShiftRequest).needed_nurses || 1;
		const neededNursesB = (b as OpenShiftRequest).needed_nurses || 1;
		if (neededNursesB > neededNursesA) return 1;
		if (neededNursesB < neededNursesA) return -1;

		// Sort by number of accepted nurses (higher number first)
		const acceptedNursesA = a.message?.accepted_nurses?.length || 0;
		const acceptedNursesB = b.message?.accepted_nurses?.length || 0;
		if (acceptedNursesB > acceptedNursesA) return 1;
		if (acceptedNursesB < acceptedNursesA) return -1;

		// Sort by number of roles (higher number first)
		const rolesA = a.message?.roles?.length || 0;
		const rolesB = b.message?.roles?.length || 0;
		if (rolesB > rolesA) return 1;
		if (rolesB < rolesA) return -1;

		const rolePriorityA = getRolePriority(a.message?.roles || []);
		const rolePriorityB = getRolePriority(b.message?.roles || []);
		if (rolePriorityB > rolePriorityA) return 1;
		if (rolePriorityB < rolePriorityA) return -1;

		// Sort by ID as final fallback for consistent ordering
		if (a.id && b.id) {
			if (a.id < b.id) return -1;
			if (a.id > b.id) return 1;
		}
		return 0;
	}, []);

	const mappedRequests = useMemo(() => {
		return filteredRequests.length === 0 ? (
			<Typography>{t("no.requests")}</Typography>
		) : (
			[...filteredRequests]
				.sort((a, b) => {
					return sortRequests(a, b);
				})
				.map((request: OpenShiftRequest | ReplaceShiftRequest, index: number) => (
					<Box key={request.id} data-testid={`request-card-${request.id}`} sx={{ width: "100%" }}>
						{Object.values(NotificationType).includes(request?.request_type) ? (
							<Box sx={{ cursor: "pointer" }} onClick={() => handleNotificationClick(request)}>
								<Recommendation
									relevantRoles={request.message.roles.map((r) => NurseRole[NurseFullRoleShort[r as keyof typeof NurseFullRoleShort]])}
									rec={recomendationFromRequests(request)}
									index={index}
									assignments={assignments}
									implementedChanges={implementedChanges}
									setImplementedChanges={setImplementedChanges}
									weekIndex={weekIndex}
									request={request}
									startingDate={hospitalData?.weeks[0]?.weeklyShifts[0]?.days[0]?.date}
									onRemoveRecommendation={(_, suggestionIdx, isCancel, nurse_id) => {
										handleRequestChange(request.id, suggestionIdx, isCancel, nurse_id);
									}}
									requestType={request.request_type}
									bonus={request.current_bonus ?? request.bonus}
									isRequestedTab={isRequestedTab}
									isTitleOnly={isTitleOnly}
									isAutoScheduler={isAutoScheduler}
									onUpdateRequest={(updateFunc: (param: OpenShiftRequest) => OpenShiftRequest) => {
										setStaffRequests((prevRequests) => {
											return [...prevRequests.map((r) => (r.id !== request.id ? r : updateFunc(r as OpenShiftRequest)))];
										});
									}}
									onTimeChange={(request, startTime, endTime) => handleRequestTimeChange(request, startTime, endTime, ChangesSource.Requests)}
								/>
							</Box>
						) : (
							<Typography>{request.request_type}</Typography>
						)}
					</Box>
				))
		);
	}, [
		filteredRequests,
		recomendationFromRequests,
		assignments,
		implementedChanges,
		setImplementedChanges,
		weekIndex,
		hospitalData?.weeks,
		setStaffRequests,
		isRequestedTab,
		isTitleOnly,
		isAutoScheduler,
		handleNotificationClick,
		handleRequestChange,
		sortRequests,
		handleRequestTimeChange,
	]);

	return !hospitalData?.weeks ? (
		<></>
	) : (
		<Box data-testid="requests-root" sx={{ p: 0, m: 0, width: "100%" }}>
			<SystemChips items={RequestsTabItems} selectedItems={[selectedChip]} onChange={handleChipChange} variant={"outlined"} />
			{mappedRequests}
		</Box>
	);
};

export default Requests;
